import {
    type FC,
    type PropsWithChildren as PWC,
    useCallback,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
    Box,
    Button,
    Checkbox,
    FullBleed,
    Heading3,
    HtmlContent,
    ScrollArea,
    Stack,
} from '@myphx-engineering/phx-design-system';

import type { PathwayTerms } from '../../../../configuration/shared';
import { useFillPrescription } from '../../../../contexts/fill/use-fill-prescription';
import { CoverageCardType } from '../../../../graphql/generated/graphql';
import { useCoverageAssistanceMutation } from '../../../../hooks/use-coverage-assistance-mutation';

import styles from './AttestationContent.module.css';

export type AttestationContentProps = {
    acceptLabel: string;
    cardType: CoverageCardType;
    declineAction?: () => void;
    declineCallback?: () => void;
    declineLabel: string;
    terms: PathwayTerms;
};

export const AttestationContent: FC<PWC<AttestationContentProps>> = ({
    acceptLabel,
    cardType,
    children,
    declineAction,
    declineCallback,
    declineLabel,
    terms,
}) => {
    const [hasAccepted, setHasAccepted] = useState(false);
    const [clickedButton, setClickedButton] = useState<
        'accept' | 'decline' | null
    >(null);
    const { t } = useTranslation('lilly');

    const { drxId } = useFillPrescription();

    const { accept, decline, loading } = useCoverageAssistanceMutation({
        cardType,
        declineCallback,
        drxId,
    });

    const handleAccept = useCallback(() => {
        setClickedButton('accept');
        void accept();
    }, [accept, setClickedButton]);

    const handleDecline = useCallback(() => {
        setClickedButton('decline');
        if (declineAction) {
            declineAction();
        } else {
            void decline();
        }
    }, [decline, declineAction, setClickedButton]);

    return (
        <>
            <Stack gap="xl" pb="xxs">
                <FullBleed breakpoint="md" color="gray" spacing="all">
                    <Stack gap="xs" pb="xs">
                        <Stack gap="md">
                            <Heading3>{terms.heading}</Heading3>
                            <Box className={styles.scrollWrapper}>
                                <ScrollArea
                                    offsetScrollbars
                                    h="60svh"
                                    type="auto"
                                    className={styles.scrollArea}
                                >
                                    <Box p="xs" className={styles.body}>
                                        <HtmlContent content={terms.body} />
                                    </Box>
                                </ScrollArea>
                            </Box>
                        </Stack>

                        <Stack
                            pt="sm"
                            pb={0}
                            data-testid="attestation-confirm-checkbox"
                        >
                            <Checkbox
                                label={t('fill.attestation.checkbox.label')}
                                onChange={setHasAccepted}
                                checked={hasAccepted}
                            />
                        </Stack>
                    </Stack>
                </FullBleed>

                <Stack gap="sm">
                    <Button
                        data-testid="attestation-accept-button"
                        disabled={!hasAccepted || loading}
                        loading={loading && clickedButton === 'accept'}
                        onClick={handleAccept}
                    >
                        {acceptLabel}
                    </Button>

                    <Button
                        data-testid="attestation-decline-button"
                        variant="outline"
                        disabled={loading}
                        loading={loading && clickedButton === 'decline'}
                        onClick={handleDecline}
                    >
                        {declineLabel}
                    </Button>
                </Stack>
            </Stack>
            {children}
        </>
    );
};
